/**
 * /plugins — Apps & Plugins. Surface Layer (/01)
 *
 * Real GET /api/surface-contracts?kind=framework_readiness|plugins. The
 * surface only renders readiness + emits ActionFrames to request adoption;
 * it never initializes any framework SDK.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Accordion,
  AccordionItem,
  Button,
  Column,
  Grid,
  InlineNotification,
  SkeletonText,
  Tag,
  Tile,
} from '@carbon/react';
import type { FrameworkReadinessEntry } from '@contracts/surface-contracts.contract';
import {
  useFrameworkReadinessContracts,
  usePluginContracts,
} from '@lib/persistence/useSurfaceContracts';
import { useDispatchStore } from '../rebuilt/action-dispatch';
import '../styles/views.scss';

export const Route = createFileRoute('/plugins')({
  component: PluginsView,
});

function PluginsView() {
  const dispatch = useDispatchStore((s) => s.dispatch);
  const framework = useFrameworkReadinessContracts();
  const plugins = usePluginContracts();
  const [adoption, setAdoption] = useState<{ status: string; detail: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function requestAdoption(f: FrameworkReadinessEntry) {
    setBusyId(f.id);
    const entry = await dispatch(
      'automations.trigger',
      { request: 'framework_adoption', framework: f.id, family: f.family },
      { label: `Request adoption · ${f.name}`, riskLevel: 'high' }
    );
    setBusyId(null);
    setAdoption({ status: entry.status, detail: entry.detail });
  }

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Apps &amp; plugins</h1>
        <p className="view__subtitle">
          Backend-owned framework readiness and plugin catalog. The surface renders and requests adoption; it never loads an SDK.
        </p>
      </header>

      <section className="view__section">
        <div className="surface-grid">
          <Grid condensed>
            <Column sm={4} md={8} lg={16}>
              <div className="view-tile__row">
                <h2 className="view__section-title">Framework readiness</h2>
                <Tag type={framework.fromBackend ? 'green' : 'cool-gray'}>
                  {framework.fromBackend ? 'live surface-contracts' : 'sign in to load'}
                </Tag>
              </div>
            </Column>
            {adoption ? (
              <Column sm={4} md={8} lg={16}>
                <InlineNotification
                  kind={adoption.status === 'error' || adoption.status === 'denied' ? 'error' : 'info'}
                  lowContrast
                  hideCloseButton
                  title={
                    adoption.status === 'approval_pending'
                      ? 'Approval pending'
                      : adoption.status === 'error' || adoption.status === 'denied'
                        ? 'Dispatch failed'
                        : 'Dispatched'
                  }
                  subtitle={adoption.detail}
                />
              </Column>
            ) : null}
            {framework.isLoading && !framework.rows ? (
              <Column sm={4} md={8} lg={16}>
                <SkeletonText paragraph lineCount={4} />
              </Column>
            ) : !framework.rows || framework.rows.length === 0 ? (
              <Column sm={4} md={8} lg={16}>
                <Tile className="view-empty">
                  <h3>No framework readiness entries</h3>
                  <p className="view-tile__label">Sign in to a provisioned workspace to load the readiness catalog.</p>
                </Tile>
              </Column>
            ) : (
              framework.rows.map((f) => (
                <Column key={f.id} sm={4} md={4} lg={5}>
                  <Tile className="view-tile">
                    <div className="view-tile__row">
                      <strong>{f.name}</strong>
                      <Tag type={f.priority === 'P1' ? 'blue' : 'cool-gray'}>{f.priority}</Tag>
                    </div>
                    <p className="view-tile__label">{f.candidate}</p>
                    <code>{f.family}</code>
                    <div className="view-actions">
                      <Button
                        size="sm"
                        kind="tertiary"
                        disabled={busyId === f.id}
                        onClick={() => void requestAdoption(f)}
                      >
                        Request adoption (approval)
                      </Button>
                    </div>
                  </Tile>
                </Column>
              ))
            )}
          </Grid>
        </div>
      </section>

      <section className="view__section">
        <div className="surface-grid">
          <Grid condensed>
            <Column sm={4} md={8} lg={16}>
              <h2 className="view__section-title">Plugin catalog</h2>
            </Column>
            <Column sm={4} md={8} lg={16}>
              {plugins.isLoading && !plugins.rows ? (
                <SkeletonText paragraph lineCount={4} />
              ) : plugins.rows && plugins.rows.length > 0 ? (
                <Accordion>
                  {plugins.rows.map((p) => (
                    <AccordionItem key={`${p.plugin_key}:${p.spoke_key}`} title={`${p.plugin_key} · ${p.spoke_key}`}>
                      <div className="view-tile__row">
                        <span>Status</span>
                        <Tag type="cool-gray">{p.status}</Tag>
                      </div>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <Tile className="view-empty">
                  <h3>No plugin catalog entries</h3>
                  <p className="view-tile__label">Sign in to a provisioned workspace to see installable plugins.</p>
                </Tile>
              )}
            </Column>
          </Grid>
        </div>
      </section>
    </div>
  );
}
